'use client';

import { createContext, useEffect, useContext, useState, useMemo } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import {
  POSSIBLE_PUBLIC_PAGES,
  PUBLIC_CENTERED_PATHNAMES,
} from '@/_constants/routing';
import { usePathname } from 'next/navigation';
import type { LayoutProviderProps } from './LayoutProvider.types';

const LayoutContext = createContext<LayoutProviderProps>({
  filterContent: null,
  isAssistantOpen: false,
  isCentered: true,
  isPublic: false,
  isStandalone: false,
  setFilterContent: () => null,
  setIsAssistantOpen: () => null,
  setTextToCopy: () => null,
  textToCopy: undefined,
});

const LayoutProvider = (props: any) => {
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [filterContent, setFilterContent] = useState<
    React.ReactNode | undefined
  >(undefined);
  const [textToCopy, setTextToCopy] = useState<string | undefined>(undefined);

  const pathname = usePathname();
  const { isLoggedIn } = useContext(UserContext);
  const isPublic = PUBLIC_CENTERED_PATHNAMES.includes(pathname);

  const isCentered: boolean =
    (!isLoggedIn && pathname === '/') ||
    PUBLIC_CENTERED_PATHNAMES.some((path: string) => pathname.startsWith(path));

  useEffect(() => {
    if (!!filterContent) setFilterContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isStandalone = useMemo(
    () => POSSIBLE_PUBLIC_PAGES.some((p: string) => pathname.startsWith(p)),
    [pathname],
  );

  return (
    <LayoutContext
      value={{
        filterContent,
        isAssistantOpen,
        isCentered,
        isPublic,
        isStandalone,
        setFilterContent,
        setIsAssistantOpen,
        setTextToCopy,
        textToCopy,
      }}
    >
      {props.children}
    </LayoutContext>
  );
};

export { LayoutContext, LayoutProvider };
